import React, { memo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import {
  Modal,
  Input,
  INPUT_THEME,
  CheckBox,
  Button,
  BUTTON_THEME,
  Card,
  CARD_THEME,
} from '@st-operations/uikit/src/v2/index.js'
import toast from '@st-operations/uikit/lib/toast'

import { offlineAttributesModal } from '../../constants'
import { classifyPrebuiltAttributes } from '../../utils'
import PredictorsScore from '../predictor-scores'

import './styles.scss'

const MODAL_STYLES = {
  content: 'modal-offline-attribute__container',
  childWrapper: 'modal-offline-attribute__wrapper',
  innerWrapper: 'modal-offline-attribute__inner-wrapper',
  headerClose: 'modal-offline-attribute__close-button',
}

const {
  attributeName,
  EMPTY_STRING,
  SAVE_CHANGES_SUCCESS_MESSAGE,
  OFFLINE_TOPICS_HEADER,
  SAVE_CHANGES,
  NO_SEARCH_DATA_MESSAGE,
} = offlineAttributesModal

const isAllSelectedInitialState = {
  b2b: false,
  cpg: false,
  ls: false,
  netwise: false,
  worksin: false,
  search: false,
}

const isPartiallySelectedInitialState = {
  b2b: false,
  cpg: false,
  ls: false,
  netwise: false,
  worksin: false,
  search: false,
}

const isOfflineAttribute = (selected) => Object.values(attributeName).includes(selected)

const OfflineAttributeModal = ({
  isOpen,
  handleClose,
  offlineAttributes,
  handleAddOfflineTopics,
  preBuiltAttrTypes,
}) => {
  const [selectedAttribute, setSelectedAttribute] = useState(attributeName.NETWISE)
  const [attributesState, setAttributesState] = useState(offlineAttributes)
  const [topicSearchText, setTopicSearchText] = useState(EMPTY_STRING)
  preBuiltAttrTypes.forEach((attr) => {
    isPartiallySelectedInitialState[attr] = false
    isAllSelectedInitialState[attr] = false
  })
  const [isAllSelected, setIsAllSelected] = useState(isAllSelectedInitialState)
  const [isPartiallySelected, setIsPartiallySelected] = useState(isPartiallySelectedInitialState)
  const [searchData, setSearchData] = useState([])
  const [predictorScoreHeader, setPredictorScoreHeader] = useState(null)

  const getTopicsForAttr = (attributeName) => {
    return attributesState.find((attr) => attr.attrName === attributeName)
  }

  useEffect(() => {
    if (isOpen) {
      setTopicSearchText(EMPTY_STRING)
      setSelectedAttribute(attributeName.NETWISE)
      setAttributesState(offlineAttributes)
    }
  }, [isOpen, offlineAttributes])

  useEffect(() => {
    if (!topicSearchText) {
      const topics = getTopicsForAttr(selectedAttribute)?.topics || []
      const isAllTopicSelected = topics.length ? topics.every((topic) => topic.selected) : false
      const isAnyTopicSelected = topics.length ? topics.some((topic) => topic.selected) : false

      if (!isAllTopicSelected && isAnyTopicSelected) {
        if (!isPartiallySelected[selectedAttribute])
          setIsPartiallySelected({
            ...isPartiallySelected,
            [selectedAttribute]: true,
          })
      } else {
        setIsPartiallySelected({
          ...isPartiallySelected,
          [selectedAttribute]: false,
        })
      }

      if (isAllTopicSelected) {
        setIsAllSelected({
          ...isAllSelected,
          [selectedAttribute]: true,
        })
      } else if (isAllSelected[selectedAttribute]) {
        setIsAllSelected({
          ...isAllSelected,
          [selectedAttribute]: false,
        })
      }
    }
  }, [attributesState])

  useEffect(() => {
    if (topicSearchText) {
      const topics = searchData
      const isAllTopicSelected = topics.length ? topics.every((topic) => topic.selected) : false
      const isAnyTopicSelected = topics.length ? topics.some((topic) => topic.selected) : false

      if (!isAllTopicSelected && isAnyTopicSelected) {
        if (!isPartiallySelected[selectedAttribute])
          setIsPartiallySelected({
            ...isPartiallySelected,
            search: true,
          })
      } else {
        setIsPartiallySelected({
          ...isPartiallySelected,
          search: false,
        })
      }

      if (isAllTopicSelected) {
        setIsAllSelected({
          ...isAllSelected,
          search: true,
        })
      } else if (isAllSelected.search) {
        setIsAllSelected({
          ...isAllSelected,
          search: false,
        })
      }
    }
  }, [searchData])

  const handleSelectTopic = (event) => {
    const selectedTopic = event.currentTarget.dataset.topic
    let topicAttribute = topicSearchText ? event.currentTarget.dataset.attribute : selectedAttribute

    setSearchData((prevState) => {
      return prevState?.map((topic) => {
        return topic.topicName === selectedTopic ? { ...topic, selected: !topic.selected } : topic
      })
    })

    setAttributesState((prevState) => {
      return prevState?.map((attribute) => {
        return attribute.attrName === topicAttribute
          ? {
              ...attribute,
              topics: attribute.topics.map((topic) => {
                return topic.topicName === selectedTopic
                  ? { ...topic, selected: !topic.selected }
                  : topic
              }),
            }
          : attribute
      })
    })
  }

  const handleTopicSearch = (value) => {
    setTopicSearchText(value)
    if (value) {
      const searchedTopics = attributesState
        .flatMap((attr) => attr.topics)
        ?.filter((topic) => topic?.topicName.toLowerCase().includes(value.toLowerCase()))
      setSearchData(searchedTopics)
    } else {
      setSearchData([])
    }
  }

  const handSelectAll = () => {
    if (topicSearchText) {
      if (isPartiallySelected.search) {
        setIsPartiallySelected({
          ...isPartiallySelected,
          search: false,
        })
        searchData.forEach((data) => {
          const { topicName, attrName } = data
          setAttributesState((prevState) => {
            return prevState?.map((attribute) => {
              return attribute.attrName === attrName
                ? {
                    ...attribute,
                    topics: attribute.topics.map((topic) => {
                      return topic.topicName === topicName ? { ...topic, selected: false } : topic
                    }),
                  }
                : attribute
            })
          })
        })
      } else {
        setIsAllSelected({
          ...isAllSelected,
          search: !isAllSelected.search,
        })

        searchData.forEach((data) => {
          const { topicName, attrName } = data
          setAttributesState((prevState) => {
            return prevState?.map((attribute) => {
              return attribute.attrName === attrName
                ? {
                    ...attribute,
                    topics: attribute.topics.map((topic) => {
                      return topic.topicName === topicName
                        ? { ...topic, selected: !isAllSelected.search }
                        : topic
                    }),
                  }
                : attribute
            })
          })
        })
      }
    } else if (isPartiallySelected[selectedAttribute]) {
      setIsPartiallySelected({
        ...isPartiallySelected,
        [selectedAttribute]: false,
      })
      setAttributesState((prevState) => {
        return prevState?.map((attribute) => {
          return attribute.attrName === selectedAttribute
            ? {
                ...attribute,
                topics: attribute.topics.map((topic) => {
                  return { ...topic, selected: false }
                }),
              }
            : attribute
        })
      })
    } else {
      setIsAllSelected({
        ...isAllSelected,
        [selectedAttribute]: !isAllSelected[selectedAttribute],
      })
      setAttributesState((prevState) => {
        return prevState?.map((attribute) => {
          return attribute.attrName === selectedAttribute
            ? {
                ...attribute,
                topics: attribute.topics.map((topic) => {
                  return { ...topic, selected: !isAllSelected[selectedAttribute] }
                }),
              }
            : attribute
        })
      })
    }
  }

  const handlePredictorScore = (selectedAttribute, selectedTopic = null) => ({
    target: { value },
  }) => {
    setAttributesState((prevState) => {
      const attributesCopy = JSON.parse(JSON.stringify(prevState))
      const attribute = attributesCopy.find(({ attrName }) => attrName === selectedAttribute)

      if (selectedTopic) {
        attribute.topics.find(
          ({ topicName }) => topicName === selectedTopic.topicName
        ).predictorScore = value
      } else {
        setPredictorScoreHeader(value)
        attribute.topics.forEach((topic) => {
          if (topic.selected) topic.predictorScore = value
        })
      }

      return attributesCopy
    })
  }

  const handleSaveChanges = () => {
    const selectedTopics = attributesState
      .flatMap((attr) => attr.topics)
      ?.filter((topic) => topic?.selected)
    handleAddOfflineTopics(selectedTopics)
    toast(SAVE_CHANGES_SUCCESS_MESSAGE)
    handleClose()
  }

  const displayData = topicSearchText
    ? attributesState
        .flatMap((attr) => attr.topics)
        ?.filter((topic) => topic?.topicName.toLowerCase().includes(topicSearchText.toLowerCase()))
    : getTopicsForAttr(selectedAttribute)?.topics || []

  const classified = classifyPrebuiltAttributes(attributesState)

  const handleSelectOfflineAttribute = (event) => {
    const currentAttrId = event.currentTarget.dataset.id
    setSearchData([])
    setSelectedAttribute(currentAttrId)
    setTopicSearchText(EMPTY_STRING)
  }

  const MenuItem = ({ attribute }) => {
    return (
      <Button
        theme={BUTTON_THEME.GHOST}
        className={classnames(
          {
            'modal-offline-attribute__menu-selected-category':
              attribute?.attrName === selectedAttribute && !topicSearchText,
          },
          'modal-offline-attribute__menu-category'
        )}
        key={attribute?.attrName}
        onClick={handleSelectOfflineAttribute}
        data-id={attribute?.attrName}
      >
        {attribute?.attrName.toUpperCase()}
        <If condition={attribute?.indicator}>
          <div className="indicator">{attribute?.indicator}</div>
        </If>
      </Button>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} classes={MODAL_STYLES}>
      <section className="modal-offline-attribute__content">
        <header className="modal-offline-attribute__header">
          <h1 className="modal-offline-attribute__header-title">{OFFLINE_TOPICS_HEADER}</h1>
          <Input
            theme={INPUT_THEME.SEARCH}
            placeholder="Quick Search"
            className="modal-offline-attribute__header-search-input"
            classes={{ inputContainer: 'modal-offline-attribute__header-search' }}
            value={topicSearchText}
            onChange={handleTopicSearch}
          />
        </header>
        <section className="modal-offline-attribute__main-section">
          <aside className="modal-offline-attribute__menu">
            <p className="modal-offline-attribute__menu-group">Offline B2B Signals</p>
            <For each="attribute" of={classified['Offline B2B Signals']}>
              <MenuItem key={attribute?.attrName} attribute={attribute} />
            </For>
            <p className="modal-offline-attribute__menu-group">Psychographics</p>
            <For each="attribute" of={classified['Psychographics']}>
              <MenuItem key={attribute?.attrName} attribute={attribute} />
            </For>
            <p className="modal-offline-attribute__menu-group">Predictors</p>
            <For each="attribute" of={classified['Predictors']}>
              <MenuItem key={attribute?.attrName} attribute={attribute} />
            </For>
          </aside>
          <div className="modal-offline-attribute__attributes">
            <div className="modal-offline-attribute__attributes-list">
              <If condition={displayData?.length}>
                <>
                  <div className="modal-offline-attribute__attributes-list-header">
                    <CheckBox
                      checked={
                        topicSearchText ? isAllSelected.search : isAllSelected[selectedAttribute]
                      }
                      label="All Attributes"
                      isIndeterminate={
                        topicSearchText
                          ? isPartiallySelected.search
                          : isPartiallySelected[selectedAttribute]
                      }
                      className="modal-offline-attribute__attributes-all"
                      onChange={handSelectAll}
                      classes={{
                        root: 'modal-offline-attribute__checkbox-size',
                        label: 'modal-offline-attribute__selected-indeterminate',
                      }}
                    />
                    {/* <If condition={!isOfflineAttribute(selectedAttribute)}>
                      <PredictorsScore
                        predictorScoreHeader={predictorScoreHeader}
                        handleChange={handlePredictorScore(selectedAttribute)}
                      />
                    </If> */}
                  </div>
                  <For each="topic" of={displayData}>
                    <Card
                      key={topic.topicName}
                      theme={CARD_THEME.NO_SHADOW}
                      className="modal-offline-attribute__attributes-card"
                    >
                      <CheckBox
                        data-topic={topic.topicName}
                        data-attribute={topic.attrName}
                        label={topic.topicName}
                        classes={{
                          label: topic.selected
                            ? 'modal-offline-attribute__selected-checkbox'
                            : EMPTY_STRING,
                        }}
                        checked={topic.selected}
                        onChange={handleSelectTopic}
                      />
                      {/* <If condition={topic.selected && !isOfflineAttribute(selectedAttribute)}>
                        <PredictorsScore
                          topic={topic}
                          handleChange={handlePredictorScore(topic.attrName, topic)}
                        />
                      </If> */}
                    </Card>
                  </For>
                </>
              </If>
              <If condition={!displayData?.length && topicSearchText}>
                <span className={'modal-offline-attribute__empty-message'}>
                  {NO_SEARCH_DATA_MESSAGE}
                </span>
              </If>
            </div>
          </div>
        </section>

        <footer className="modal-offline-attribute__footer">
          <div className="modal-offline-attribute__footer-actions">
            <Button isLoading={false} onClick={handleSaveChanges}>
              {SAVE_CHANGES}
            </Button>
          </div>
        </footer>
      </section>
    </Modal>
  )
}

OfflineAttributeModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleAddOfflineTopics: PropTypes.func.isRequired,
  offlineAttributes: PropTypes.array,
  preBuiltAttrTypes: PropTypes.array,
  isOpen: PropTypes.bool,
}

OfflineAttributeModal.defaultProps = {
  offlineAttributes: [],
  preBuiltAttrTypes: [],
  isOpen: false,
}

export default memo(OfflineAttributeModal)
